import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Eye, EyeClosed } from "@assets";
import { TextInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AnalyticsEvent, StorageKey } from "@ctypes";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useOnboardingSavePoint, useSignUp } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { Analytics } from "@services";
import { validationSchema } from "./content";
import { PasswordValidator } from "./PasswordValidator";
import { ReferralCodeSection } from "./ReferralCodeSection";
import { SignUpFormValues } from "./types";

export const SignUpRoute = () => {
  const [showValidator, setShowValidator] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { showErrorBanner } = useErrorBanner();

  const [navigateToNextRoute] = useOnboardingSavePoint();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<SignUpFormValues>({
    defaultValues: {
      email:
        useEntryIntakeStore.getState().intake.email || localStorage.getItem(StorageKey.Email) || "",
      password: "",
      promotionCode: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [signUp] = useSignUp();

  const emailController = register("email");
  const passwordController = register("password");

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async ({ email, password, promotionCode }: SignUpFormValues) => {
    try {
      setLoading(true);

      localStorage.setItem(StorageKey.Email, email);

      const { data } = await signUp({
        variables: {
          email,
          password,
          promotionCode,
        },
      });

      const accessToken = data?.registerUser.sessionJwt;

      if (accessToken) {
        localStorage.setItem(StorageKey.Token, accessToken);

        // The lead conversion, same as the phone-first path in
        // PhoneLoginInputRoute - an account exists from here on. No phone
        // number: this flow is email and password only.
        Analytics.track(AnalyticsEvent.SignUp, undefined, { email_address: email });

        await navigateToNextRoute();
      } else {
        showErrorBanner();
      }
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.EntrySignUpTitle),
    subtitle: i18n.t(Translations.EntrySignUpSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.SignUpRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
  };

  return (
    <EntryStepLayout {...props}>
      <TextInput
        {...emailController}
        label={i18n.t(Translations.LoginRouteEmailLabel)}
        value={watch("email")}
        onChangeText={(text) => setValue("email", text, constants.form)}
        error={errors.email?.message}
        placeholder={i18n.t(Translations.SignUpRouteEmailPlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...passwordController}
        label={i18n.t(Translations.LoginRoutePasswordLabel)}
        value={watch("password")}
        onChangeText={(text) => setValue("password", text, constants.form)}
        type={showPassword ? "text" : "password"}
        error={errors.password?.message}
        rightIcon={showPassword ? <EyeClosed /> : <Eye />}
        onRightIconClick={toggleShowPassword}
        placeholder={i18n.t(Translations.SignUpRoutePasswordPlaceholder)}
        onFocusCallback={() => setShowValidator(true)}
        onBlurCallback={() => setShowValidator(false)}
      />

      <ReferralCodeSection
        value={watch("promotionCode")}
        onChangeValue={(value) => setValue("promotionCode", value, constants.form)}
      />

      {showValidator || watch("password").length > 0 ? (
        <PasswordValidator password={watch("password")} />
      ) : null}
    </EntryStepLayout>
  );
};
